import { useI18n } from "@/lib/i18n/i18n_provider";
import { MeetingRoomsFilter } from "@/offering/template_offering_listing_meeting_room";
import { intersection, uniq } from "lodash";
import { useState, useCallback } from "react";
import { LocationFilterCity } from "../components/location_picker_dropdown";
import { FilterLocation } from "@/data/partner_offering";

export function useLocationPickerState(
  locations: FilterLocation[],
  values: Pick<MeetingRoomsFilter, "cities" | "locationIDs">,
  isMultiSelect = true
) {
  const { fbt } = useI18n();

  const citiesValue = values.cities || [];
  const locationIDsValue = values.locationIDs || [];
  const [selectedCities, setSelectedCities] = useState<string[]>(citiesValue);
  const [selectedLocationIDs, setSelectedLocationIDs] =
    useState<string[]>(locationIDsValue);

  const citiesOfSelectedLocations = uniq(
    locations.filter((l) => locationIDsValue.includes(l.id)).map((l) => l.city)
  );

  const displayedValue =
    citiesOfSelectedLocations.length === 1
      ? `${citiesOfSelectedLocations[0]}`
      : citiesOfSelectedLocations.length === 2
      ? `${citiesOfSelectedLocations[0]}, ${citiesOfSelectedLocations[1]}`
      : citiesOfSelectedLocations.length > 2
      ? fbt(
          fbt.param("numberOfCities", citiesOfSelectedLocations.length) +
            " cities, " +
            fbt.param("numberOfBuildings", locationIDsValue.length) +
            " buildings"
        )
      : undefined;

  const handleSelectCity = (city: string, locationIDs: string[]) => {
    const updatedCities = [...selectedCities];

    if (selectedCities.includes(city)) {
      const index = updatedCities.findIndex((c) => c === city);
      if (index !== -1) {
        updatedCities.splice(index, 1);
        setSelectedCities(updatedCities);
      }
      setSelectedLocationIDs(
        selectedLocationIDs.filter((id) => !locationIDs.includes(id))
      );
    } else {
      updatedCities.push(city);
      setSelectedCities(updatedCities);
      locationIDs.forEach((id) => {
        if (!selectedLocationIDs.includes(id)) {
          setSelectedLocationIDs((value) => {
            const updated = [...value];
            updated.push(id);
            return updated;
          });
        }
      });
    }
  };

  const handleSelectLocation = (
    locationId: string,
    fromCity: LocationFilterCity
  ) => {
    if (!isMultiSelect) {
      if (selectedLocationIDs.includes(locationId)) {
        setSelectedLocationIDs([]);
        setSelectedCities([]);
      } else {
        setSelectedLocationIDs([locationId]);
        setSelectedCities([fromCity.label]);
      }
      return;
    }

    const updatedLocationIDs = [...selectedLocationIDs];
    const updatedCities = [...selectedCities];

    if (selectedLocationIDs.includes(locationId)) {
      const locationIndex = updatedLocationIDs.findIndex(
        (c) => c === locationId
      );
      if (locationIndex !== -1) {
        updatedLocationIDs.splice(locationIndex, 1);
        setSelectedLocationIDs(updatedLocationIDs);
      }

      const cityIndex = updatedCities.findIndex((c) => c === fromCity.label);
      if (cityIndex !== -1) {
        updatedCities.splice(cityIndex, 1);
        setSelectedCities(updatedCities);
      }
    } else {
      updatedLocationIDs.push(locationId);
      setSelectedLocationIDs(updatedLocationIDs);

      if (
        intersection(
          updatedLocationIDs,
          fromCity.locations.map((l) => l.id)
        ).length === fromCity.locations.length
      ) {
        updatedCities.push(fromCity.label);
        setSelectedCities(updatedCities);
      }
    }
  };

  const handleClearSelection = useCallback(() => {
    setSelectedCities([]);
    setSelectedLocationIDs([]);
  }, []);

  return {
    displayedValue,
    selectedCities,
    selectedLocationIDs,
    handleSelectCity,
    handleSelectLocation,
    handleClearSelection,
  };
}
